import { FFH_SERVICE_INT } from '@hub/free-form-hub/config';
import { useTranslation } from 'next-i18next';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { useLoader } from '../loader/useLoader';
import { useState } from 'react';

type FileData = {
  uploadId: string;
};

export type Status = 'pending' | 'completed' | 'failed';

type UploadStatus = {
  status: Status;
};

export const useFfhUpload = (waitForNbs = false) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(['free-form-hub/freeFormHub', 'common']);
  const { showLoader, hideLoader } = useLoader();
  const [uploadedFileId, setUploadedFileId] = useState<string>(undefined);
  const [uploadStatus, setUploadStatus] = useState<Status>();

  const showError = (errorMsg: string) => {
    addToast({
      content: t(`${errorMsg}`, { ns: 'free-form-hub/freeFormHubCommon' }),
      type: ToastType.ERROR,
      position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
    });
  };

  const checkUploadStatus = async (fileId: string, delay: number, retryCount: number) => {
    const maxRetries = 60;
    const maxDelayTime = 10_000;

    if (!fileId) {
      return;
    }

    if (retryCount >= maxRetries) {
      throw new Error('Upload status check failed: Maximum retries reached');
    }

    try {
      const statusResponse = await fetch(`${FFH_SERVICE_INT}/file/${fileId}/status?stream=true`);
      if (!statusResponse.ok) {
        throw new Error('Failed to fetch upload status');
      }

      const statusResult = (await statusResponse.json()) as UploadStatus;
      const { status } = statusResult;

      if (status === 'completed') {
        setUploadStatus('completed');
        if (!waitForNbs) {
          hideLoader();
        }
      } else if (status === 'pending') {
        const newDelay = Math.min(delay + 1000, maxDelayTime);
        setTimeout(() => checkUploadStatus(fileId, newDelay, retryCount + 1), newDelay);
      } else {
        throw new Error('Upload status: File upload failed');
      }
    } catch (error) {
      hideLoader();
      showError('COMMON.FILE_UPLOAD.CONVERSION_ERROR');
      setUploadStatus('failed');
    }
  };

  const upload = async (file: File): Promise<void> => {
    showLoader();
    setUploadStatus('pending');

    const formData = new FormData();
    formData.append('file', file, file.name);

    try {
      const response = await fetch(`${FFH_SERVICE_INT}/file/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('File upload failed');
      }

      const fileMetadata = (await response.json()) as FileData;
      const fileId = fileMetadata.uploadId;
      setUploadedFileId(fileId);

      void checkUploadStatus(fileId, 0, 0);
    } catch (error) {
      hideLoader();
      showError('COMMON.FILE_UPLOAD.UPLOAD_FAILED');
      setUploadStatus('failed');
    }
  };

  return { upload, checkUploadStatus, uploadedFileId, uploadStatus };
};
